<template>
  <div
    class="progress-container pa-2 bg-white dark:bg-gray-400 rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl"
  >
    <v-row align="center" class="flex items-center justify-between w-full">
      <v-list-item-avatar class="mr-3">
        <v-icon :color="getStatusColor(cardTexts.auto_build)" size="24">
          {{ getStatusIcon(cardTexts.auto_build) }}
        </v-icon>
      </v-list-item-avatar>

      <v-list-item-content class="flex-grow">
        <div class="d-flex flex-column">
          <!-- Title and Status Row -->
          <div class="d-flex align-center justify-space-between mb-2">
            <v-list-item-title
              class="font-weight-bold fontTitle-large"
              color="primary"
            >
              <v-icon color="primary" class="mr-2">
                mdi-{{ getQrTypes(info.type).icon }}
              </v-icon>
              {{ info.title || "کارت ویزیت" }}
            </v-list-item-title>

            <!-- Status Chip and Notification Bell -->
            <div class="d-flex align-center">
              <!-- Notification Bell -->
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="mr-2 notification-bell"
                    :class="{ shake: newNotifications && !showNotifications }"
                    @click="toggleNotificationModal()"
                  >
                    <v-icon size="20" color="primary">mdi-bell</v-icon>
                    <v-badge
                      v-if="newNotifications && !showNotifications"
                      color="red"
                      dot
                      offset-x="10"
                      offset-y="1"
                    />
                  </v-btn>
                </template>
                <span>اعلان‌ها</span>
              </v-tooltip>

              <v-tooltip bottom color="error">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    small
                    class="mr-2"
                    @click="removeJob()"
                  >
                    <v-icon size="20" color="red">mdi-cancel</v-icon>
                  </v-btn>
                </template>
                <span>لغو ساخت کیو آر</span>
              </v-tooltip>

              <!-- Status Chip -->
              <v-chip
                small
                :color="getStatusColor(cardTexts.auto_build)"
                text-color="white"
                class="status-chip"
              >
                {{ getStatusText(cardTexts.auto_build) }}
              </v-chip>
            </div>

            <!-- Link Row -->
            <v-list-item-subtitle class="text-caption mb-2" v-if="isMobile">
              <v-icon small class="mr-1">mdi-link</v-icon>
              <span class="font-family-monospace">{{
                `qrland.in/${info.identifier}` || info.qr
              }}</span>
            </v-list-item-subtitle>

            <!-- Refresh Button Row -->
            <div class="d-flex align-center justify-start" v-if="isMobile">
              <v-tooltip
                top
                v-if="
                  cardTexts.auto_build !== 'success' &&
                  cardTexts.auto_build !== 'failed'
                "
                :color="getStatusColor(cardTexts.auto_build)"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="getStatusColor(cardTexts.auto_build)"
                    fab
                    Class="refresh-btn"
                    x-small
                    icon
                    :disabled="loading"
                    style="width: 25px; height: 25px; position: relative"
                    :loading="loading"
                    v-bind="attrs"
                    v-on="on"
                    @click="retryQr()"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>{{
                  status === "process"
                    ? "به‌روزرسانی "
                    : status === "failed"
                    ? " تلاش مجدد"
                    : "به‌روزرسانی"
                }}</span>
              </v-tooltip>
            </div>
          </div>

          <!-- Link Row -->
          <v-list-item-subtitle class="text-caption mb-2" v-if="!isMobile">
            <v-icon small class="mr-1">mdi-link</v-icon>
            <span class="font-family-monospace">{{
              `qrland.in/${info.identifier}` || info.qr
            }}</span>
          </v-list-item-subtitle>

          <!-- Refresh Button Row -->
          <div class="d-flex align-center justify-end" v-if="!isMobile">
            <v-tooltip
              top
              v-if="
                cardTexts.auto_build !== 'success' &&
                cardTexts.auto_build !== 'failed'
              "
              :color="getStatusColor(cardTexts.auto_build)"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :color="getStatusColor(cardTexts.auto_build)"
                  fab
                  Class="refresh-btn"
                  x-small
                  icon
                  :disabled="loading"
                  style="width: 25px; height: 25px; position: relative"
                  :loading="loading"
                  v-bind="attrs"
                  v-on="on"
                  @click="retryQr()"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>{{
                status === "process"
                  ? "به‌روزرسانی"
                  : status === "failed"
                  ? " تلاش مجدد"
                  : "به‌روزرسانی"
              }}</span>
            </v-tooltip>
          </div>
        </div>
      </v-list-item-content>

      <v-list-item-action class="ml-0">
        <v-progress-circular
          v-if="cardTexts.auto_build === 'process'"
          size="64"
          :value="progressValue"
          height="30"
          striped
          rounded
          :color="getProgressColor(progressValue)"
          class="rainbow-progress"
          width="6"
        >
          <span class="text-caption font-weight-bold text-xs font-bold">
            %{{ persian(Math.ceil(progressValue)) }}
          </span>
        </v-progress-circular>

        <v-btn
          v-if="cardTexts.auto_build === 'success'"
          icon
          small
          color="success"
          @click="viewQr(info)"
        >
          <v-icon>mdi-eye</v-icon>
        </v-btn>
        <v-btn
          v-else-if="cardTexts.auto_build === 'failed'"
          icon
          small
          color="error"
          @click="tryprocess(cardTexts.auto_id)"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-list-item-action>
    </v-row>
    <!-- Notification Modal -->
    <custom-popup
      v-model="showNotifications"
      :cons="{ title: `اعلان‌های ${info.title || 'کیوآر'}` }"
      transition="slide-y-transition"
      hide-confirm
      max-width="400"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>اعلان‌ها</span>
          <v-btn
            v-if="notifications.length > 0"
            icon
            small
            @click="clearAllNotifications"
          >
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-list class="notification-list">
            <v-list-item
              v-for="(notification, index) in notifications"
              :key="notification.id"
              class="notification-item"
              :class="getNotificationClass(notification.type)"
            >
              <v-list-item-avatar>
                <v-icon :color="getNotificationColor(notification.type)">
                  {{ getNotificationIcon(notification.type) }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ notification.message }}
                </v-list-item-title>
                <v-list-item-subtitle
                  class="text-caption text--secondary time-box"
                >
                  {{ formatTime(notification.timestamp) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <!-- Eye icon for viewed/unviewed status -->
              <v-list-item-action>
                <v-icon color="grey" size="16">
                  {{ getEyeIcon(notification.id) }}
                </v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="notifications.length === 0">
              <v-list-item-content>
                <v-list-item-title class="text-center text--secondary">
                  هیچ اعلانی وجود ندارد
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </custom-popup>
  </div>
</template>

<script>
import axios from "axios";
import JwtService from "@/core/plugins/TitleRibbon/assets/js/jwt.service";
import customPopup from "../../../../core/plugins/TitleRibbon/plugins/popup/customPopup.vue";

export default {
  name: "progress_createauto2",
  components: {
    customPopup,
  },
  props: {
    info: { type: Object },
  },
  data() {
    return {
      checkIntervalId: null,
      oldValueStatus: "",
      aboutUs: "",
      type: "visitcard",
      progressInterval: null,
      images: null,
      progressValue: 0,
      status: "process", // 'loading', 'error', 'completed'
      loading: false,
      url_: "",
      hinturl:
        "لیتک سایت باید به صورت رویرو وارد شود https://terminalads.com :  ",
      url: "",
      flagUrl: false,
      errorurl: "",
      urlRules: [
        (v) => !v || v.length <= 100 || "حداکثر ۱۰۰ کاراکتر مجاز است",
        (v) =>
          !v ||
          this.isURL(v) ||
          " https://terminalads.com :   لینک سایت باید بصورت روبرو وارد شود",
      ],
      Ai_Text: "",
      flag: false,
      mylastQr: false,
      qrId: 0,
      qrloading: false,
      qrselectloading: false,
      selectedQr: null,
      QrData: [],
      countData: 0,
      selectedRows: false,
      tableProps: {
        page: 1,
        length: 100000,
        search: "",
        sort: "desc",
      },
      search: "",
      pagination: {},
      list: [],
      firstPage: false,
      currentPage: 1,
      loadingdata: false,
      perPage: 100000,
      page: null,
      headers: [
        { text: "ردیف", value: "index" },
        { text: "عنوان", value: "title" },
        { text: "لینک", value: "link" },
      ],
      isOldData: false,
      oldData: [],
      file: null,
      createdId: "",
      // url: "https://robot-api.terminalads.com/api",
      isLastPage: false,
      indexAt: -1,
      categoryName: "",
      defaultCatName: "",
      img: {
        items: {},
        categories: [],
      },
      qrSvgDataUrl: "",
      loading: false,
      orders: [
        { name: "userName" },
        { name: "workField" },
        { name: "aboutUs" },
        { name: "extraFields" },
        {
          name: "video_gallery",
        },
        { name: "photo" },
        { name: "phoneNumber" },
        { name: "fax" },
        { name: "location" },
        { name: "codePostal" },
        { name: "email" },
        { name: "website" },
      ],
      theme: "MobileViwer",
      destItems: [],
      dtUrl: "",
      Textbusiness: "",
      perfix_business: "",
      qrDesign: {
        color: "#000000",
        background: "#FFFFFF",
        pattern: "square",
      },
      domainId: 5,
      createdDomain: "qrland.in/",
      rules: [
        (v) => !!v || "فیلد فعالیت الزامی است",
        (v) =>
          !v || this.spellChecker(v) || "متن وارد شده باید به زبان فارسی باشد!",
        (v) => (v && v.length >= 2) || "حداقل دو کاراکتر باید وارد شود!",
        (v) => {
          if (this.perfix_business !== "")
            return (
              (v && v.trim().split(" ").length >= 1) ||
              "حداقل یک کلمه وارد کنید"
            );
          else
            return (
              (v && v.trim().split(" ").length >= 2) ||
              "حداقل دو کلمه وارد کنید"
            );
        },
        (v) => (v && v.length <= 100) || "حداکثر ۱۰۰ کاراکتر مجاز است",
      ],
      loading: false,
      result: [],
      translatedText: "",
      business: "",
      tabs: 0,
      type: "",
      count: 1000,
      helpData: [],
      serviceName: "Qrland",
      componentName: "create_QrproductAuto",
      // Notification related data
      showNotifications: false,
      notifications: [],
      viewedNotifications: [],
      // QrTypes: {},
      cardTexts: {
        auto_build: "process",
        auto_id: null,
        advanceSettings: {
          hasManifestIcons: true,
          viewConfigs: {
            autoCsvDownload: false,
            showCClubOnload: true,
            forcedSubmission: "optional",
          },
          manifest: {
            icon48: "",
            icon144: "",
            screenshotWide: "",
            screenshot600: "",
          },
        },
        customerClub: {
          title: "عضویت در باشگاه مشتریان",
          description: "",
          configs: {
            patternId: 487,
            smsType: "pattern",
            isActive: true,
            message: {
              data1: {
                state: "userInfo",
                text: "name",
              },
              data2: {
                state: "userInfo",
                text: "lastName",
              },
            },
          },
        },
        userName: "",
        workField: "",
        aboutUs: "",
        video_single: null,
        video_gallery: [
          {
            name: "گالری ویدئو",
            images: [
              {
                name: "خوش آمدگویی",
                title: "",
                url: "https://www.aparat.com/video/video/embed/videohash/a89rkt7/vt/frame",
                file_link: "https://www.aparat.com/v/a89rkt7",
                description: "",
                link: "https://www.aparat.com/v/a89rkt7",
                id: 15,
              },
            ],
          },
        ],
        photo: {
          gallery: [
            {
              name: "",
              images: [],
            },
          ],
          headBackground: "/media/defaults/visitFirstDefault.webp",
          profile: "/media/defaults/qrLogo.webp",
          transition: "scale",
          hasWelcome: false,
          hasGif: false,
          hasGame: false,

          previewerQr: {
            previewer: {
              gif: "",
              game: {},
              transition: "scale",
            },
          },
        },
        extraFields: [
          {
            icon: "",
            name: "",
            value: "",
          },
        ],
        phoneNumber: [
          {
            number: "",
          },
        ],
        fax: "",
        location: "",
        codePostal: "",
        email: "",
        website: "",
        visitName: "کارت ویزیت",
        pallete: {
          headColor: "linear-gradient(to top, #AA7630FF, #EBCB63FF)",
          bodyColor: "linear-gradient(to top right , #AA7630FF, #EBCB63FF)",
          textColor: "#ffffff",
          borderColor: "#393939",
        },
        organizationName: "",
        subOrgan: "",
        companyNumber: "",
        city: "",
        province: "",
        country: "",
        socialMedia: {},
        center: [51.420296, 35.732379],
        coordinates: [51.420296, 35.732379],
        address: "",
        apiKey:
          "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6ImZhZjI0ZjA0Y2U2NTQ2ZDAyYzMwNjBmMzAxYWY0NTMxYjhkOTEwODVhMDQ3MGY1ZDc3YjYwY2M4OGRiNjVkZjZkMjIyZWNkZjNmMTJkYmExIn0.eyJhdWQiOiIyMDI5NSIsImp0aSI6ImZhZjI0ZjA0Y2U2NTQ2ZDAyYzMwNjBmMzAxYWY0NTMxYjhkOTEwODVhMDQ3MGY1ZDc3YjYwY2M4OGRiNjVkZjZkMjIyZWNkZjNmMTJkYmExIiwiaWF0IjoxNjcwNjg5MDc4LCJuYmYiOjE2NzA2ODkwNzgsImV4cCI6MTY3MzE5NDY3OCwic3ViIjoiIiwic2NvcGVzIjpbImJhc2ljIl19.h-KYgigwSQbp9KYjIjWgBLQRpMy6ABa40dyF4pF99UmndnhDNs-fckFePIdRPkm7GlggWm9yKphj_TJ5b9TyA8m1_gNarjRiPzhAS5ZNGNUaSfnGHA1cUbc71DuUKgohAe2sOmXUkKin0q-ErzDdCczJjLVc218dRkCxLuJIrAY5yZ5yVgK79-Q6misDYP3yLpWaV5uDA3a1VzXppq4G9hm0GCGmsef_WryEx9z1fFuOzoVbuNj2yEKn3mBi2vAyKvV1HRMKMoXy_KLTk8xy-GsSQArOxKvw4Nk8Nvuks0WzSlLygNncQozltpm5Z4kueep-0TrI4xzx6uSjuk20vg",
      },
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    newNotifications() {
      const hasNew = this.notifications.some(
        (notification) => !this.viewedNotifications.includes(notification.id),
      );
      return hasNew;
    },

    // Computed property for better reactivity
    notificationStatus() {
      return this.notifications.map((notification) => ({
        id: notification.id,
        isViewed: this.viewedNotifications.includes(notification.id),
      }));
    },
    statusText() {
      switch (this.status) {
        case "failed":
          return "ساخت کیو آر با خطا مواجه شده";
        case "process":
          return "در حال ساخت کیو آر";
        case "success":
          return "تکمیل شده";
        default:
          return "در حال ساخت";
      }
    },
    statusClass() {
      return {
        "error-text": this.status === "failed",
        "loading-text": this.status === "process",
        "completed-text": this.status === "success",
      };
    },
    progressColor() {
      switch (this.status) {
        case "failed":
          return "red";
        case "process":
          return "blue";
        case "success":
          return "green";
        default:
          return "blue";
      }
    },
  },
  mounted() {
    this.updateCardTextsByType();
    this.progressInterval = setInterval(() => {
      if (this.cardTexts.auto_build === "success") {
        clearInterval(this.progressInterval);
        return;
      }
      this.fetchProgress();
    }, 20000);
    this.startVariableCheck();

    // Initialize notifications
    this.initializeNotifications();
  },
  beforeDestroy() {
    clearInterval(this.progressInterval);
    this.stopVariableCheck();
  },
  watch: {
    "cardTexts.auto_build"(newVal) {
      this.status = newVal;
      this.$nextTick(() => this.$forceUpdate());
    },
  },
  methods: {
    removeJob() {
      if (!this.info?.id || !this.info.data?.data?.auto_id) {
        this.$toast.error("داده‌های مورد نیاز برای لغو فرآیند موجود نیست.");
        return;
      }

      if (this.$modal.yesNo("از لغو کیو آر موردنظرتان مطمئن هستید؟")) {
        axios
          .delete(
            this.$aiApi + "/process/delete/" + this.info.data.data.auto_id,
            {
              data: {
                qr_id: this.info.id,
              },
              headers: this.header(),
            },
          )
          .then(() => {
            this.$toast.success("فرآیند حذف با موفقیت انجام شد.");
            this.$emit("refreshBuildingList");
          })
          .catch((error) => {
            console.error("Error removing job:", error);
            this.$toast.error("خطا در حذف فرآیند.");
          });
      }
    },
    async fetchProgress() {
      this.loading = true;
      try {
        // Update cardTexts with info data
        if (this.info?.data?.data) {
          this.cardTexts = { ...this.cardTexts, ...this.info.data.data };
        }
        if (
          this.info?.data?.data?.auto_id !== null &&
          this.info?.data?.data?.auto_id !== undefined
        ) {
          this.checkProcess(this.info?.data?.data?.auto_id);
          this.$nextTick(() => {
            this.$forceUpdate();
          });

          // this.$toast.success("فرآیندساخت با موفقیت ادامه دارد.");
        }
        //  this.tryprocess(this.info.data.data.auto_id);

        // this.progressValue = response.data.progress;
        // this.status = response.data.status;
      } catch (error) {
        console.error("Error fetching progress:", error);
        this.status = "failed";
        // this.progressValue = 0;
        // this.$toast.error("خطا در دریافت اطلاعات از API");
      } finally {
        this.loading = false;
      }
    },
    updateCardTextsByType() {
      if (this.type === "product") {
        this.cardTexts = {
          ...this.cardTexts,
          audiosGallery: [],
          pallete: {
            headColor: "linear-gradient(to top left , #D40075FF, #2F0743FF)",
            bodyColor: "linear-gradient(to top left , #006064FF, #03A9F4FF)",
            textColor: "#ffffff",
            borderColor: "#393939",
          },
          Features: {
            show: "accordion",
            details: [
              {
                name: "ویژگی های محصول",
                showIt: false,
                value: [
                  {
                    label: "",
                    value: "",
                    icon: "",
                    name: "",
                    link: "",
                  },
                  {
                    label: "",
                    value: "",
                    icon: "",
                    name: "",
                    link: "",
                  },
                  {
                    label: "",
                    value: "",
                    icon: "",
                    name: "",
                    link: "",
                  },
                  {
                    label: "",
                    value: "",
                    icon: "",
                    name: "",
                    link: "",
                  },
                ],
              },
            ],
          },
          singleProduct: {
            name: "",
            description: "",
            price: 0,
            discount: "0",
            payLink: "",
            btnSetting: {
              title: "خرید",
              icon: undefined,
              background: "linear-gradient(to top left , #D81A1AFF, #2115A7FF)",
              color: "white",
            },
            extra: [
              {
                label: "",
                value: "",
                icon: "",
                name: "",
                link: "",
              },
            ],
            image: "",
            color: undefined,
            arm: [],
          },
          visitName: "معرفی محصول",
          isGradient: false,
          phoneNumber: [
            { number: "", text: "" },
            { number: "", text: "" },
          ],
          extraFields: [{ icon: "", name: "", value: "" }],
          companyNumberName: "",
          photo: {
            gallery: [
              {
                name: "",
                images: [],
              },
            ],
            background: this.$cdnUrl + "/upload/64b2bb1c4ad38.webp",
            headBackground: this.$cdnUrl + "/upload/64b2abe4bab25.webp",
            profile: this.$cdnUrl + "/upload/64b2abe4bab25.webp",
            transition: "scale",
            hasWelcome: false,
            hasGif: false,
            hasGame: false,

            previewerQr: {
              previewer: {
                gif: "",
                game: {},
                transition: "scale",
              },
            },
          },
          VideoGallery: {
            title: "گالری ویدئو",
            description: "از ویدئوهای مجموع ما دیدن فرمایید.",
            sectionColor: {
              backGround: "",
              text: "",
            },
          },
          video_gallery: [
            {
              name: "گالری",
              images: [
                {
                  name: "خوش آمدگویی",
                  title: "",
                  file_link:
                    "https://persian7.asset.aparat.com/aparat-video/1d2d2d8b5fea10b3cbda4fb6e2c45c7653042587-1080p.apt?wmsAuthSign=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0b2tlbiI6ImFiMjVjNDA3ZmJjMzU4NTRkNzAyYjU4ZTY0ZDc2ZjVhIiwiZXhwIjoxNzUyNTkzNzY1LCJpc3MiOiJTYWJhIElkZWEgR1NJRyJ9.-93y_H0A24w0SV8KdwQMw4w3QTgNmlaGEPn_pqgYCgk",
                  description: "",
                  link: "",
                  id: 15,
                },
              ],
            },
          ],
          arm: [],
        };
      } else if (this.type === "label") {
        this.cardTexts = {
          ...this.cardTexts,
          audiosGallery: [],
          FeedBack: {
            title: "بازخورد شما",
            description:
              "لطفا با پاسخ گویی به سوالات زیر مارا در خدمات بهتر راهنمایی فرمایید",
            sectionColor: {
              backGround: "linear-gradient(to right , #FFFFFFFF, #FFFFFFFF)",
              text: "#fff",
            },
            type: "text",
            items: [
              {
                question: `چقدر از خدمات ارائه شده راضی بوده اید؟`,
                answer: "",
                value: null,
                description: false,
                forceDesc: false,
              },
              {
                question: `خدمات ارائه شده  چه میزان از نیازهای شما را پوشش داده است؟`,
                answer: "",
                value: null,
                description: false,
                forceDesc: false,
              },
              {
                question:
                  "میزان هزینه پرداختی بابت سرویس دریافتی چقدر معقول بوده است؟",
                answer: "",
                value: null,
                description: false,
                forceDesc: false,
              },
              {
                question: "برای  بهبود خدمات نظر خود را با ما درمیان بگذارید.",
                answer: "",
                value: null,
                description: true,
                forceDesc: false,
              },
            ],
          },
          order: [
            { name: "price" },
            { name: "bPrice" },
            { name: "cPrice" },
            { name: "discount" },
            { name: "production" },
            { name: "expiration" },
            { name: "weigth" },
            { name: "size" },
            { name: "model" },
            { name: "series" },
            { name: "country" },
            { name: "producer" },
            { name: "brand" },
            { name: "discriptions" },
          ],
          pallete: {
            headColor: "linear-gradient(to top left , #00BCD4FF, #2F0743FF)",
            bodyColor: "linear-gradient(to top left , #006064FF, #03A9F4FF)",
            textColor: "#000",
            borderColor: "#393939",
          },
          label: {
            name: { text: "نام مجموعه", line: false },
            labelName: { text: "نام محصول", line: false },
            price: { text: 10, line: false },
            bPrice: { text: 10, line: false },
            cPrice: { text: 10, line: false },
            discount: { text: 10, line: false },
            production: { text: "", line: false },
            expiration: { text: "", line: false },
            weigth: { text: "", line: false },
            size: { text: "", line: false },
            model: { text: "", line: false },
            series: { text: "", line: false },
            country: { text: "", line: false },
            producer: { text: 10, line: false },
            brand: { text: 10, line: false },
            discriptions: { text: 10, line: false }, //توضیحات
            img: { text: false, line: false },
            video: { text: false, line: false },
            arm: { text: false, line: false },
          },
          visitName: "لیبل محصول",
          isGradient: false,
          phoneNumber: [
            { number: "", text: "" },
            { number: "", text: "" },
          ],
          extraFields: [{ icon: "", name: "", value: "" }],
          companyNumberName: "",
          photo: {
            gallery: [
              {
                name: "",
                images: [],
              },
            ],
            background: this.$cdnUrl + "/upload/64b2bb1c4ad38.webp",
            headBackground: this.$cdnUrl + "/upload/64b2abe4bab25.webp",
            profile: this.$cdnUrl + "/upload/64b2abe4bab25.webp",
            transition: "scale",
            hasWelcome: false,
            hasGif: false,
            hasGame: false,

            previewerQr: {
              previewer: {
                gif: "",
                game: {},
                transition: "scale",
              },
            },
          },
          VideoGallery: {
            title: "گالری ویدئو",
            description: "از ویدئوهای مجموع ما دیدن فرمایید.",
            sectionColor: {
              backGround: "",
              text: "",
            },
          },
          video_gallery: [
            {
              name: "گالری",
              images: [
                {
                  name: "خوش آمدگویی",
                  title: "",
                  file_link:
                    "https://persian7.asset.aparat.com/aparat-video/1d2d2d8b5fea10b3cbda4fb6e2c45c7653042587-1080p.apt?wmsAuthSign=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0b2tlbiI6ImFiMjVjNDA3ZmJjMzU4NTRkNzAyYjU4ZTY0ZDc2ZjVhIiwiZXhwIjoxNzUyNTkzNzY1LCJpc3MiOiJTYWJhIElkZWEgR1NJRyJ9.-93y_H0A24w0SV8KdwQMw4w3QTgNmlaGEPn_pqgYCgk",
                  description: "",
                  link: "",
                  id: 15,
                },
              ],
            },
          ],
          arm: [],
        };
      }
    },
    startVariableCheck() {
      this.checkIntervalId = setInterval(() => {
        if (
          this.oldValueStatus === this.cardTexts.auto_build &&
          this.cardTexts.auto_build === "process"
        ) {
          this.tryprocess(this.info.data.data.auto_id);
        }
        this.oldValueStatus = this.info.data.data.auto_build;
      }, 1200000);
    },
    stopVariableCheck() {
      if (this.checkIntervalId) {
        clearInterval(this.checkIntervalId);
        this.checkIntervalId = null;
      }
    },
    viewQr(item) {
      // Open QR in new tab
      window.open(`https://qrland.in/${item.identifier}`, "_blank");
    },
    retryQr() {
      // Retry failed QR
      // this.$toast.info("تلاش مجدد برای ساخت کیوآر کد...");
      this.handleToast({
        type: "info",
        message: "تلاش مجدد برای ساخت کیوآر کد...",
      });
      this.fetchProgress();
    },

    getProgressColor(progress) {
      if (progress < 30) return "brown";
      if (progress < 50) return "red";
      if (progress < 70) return "orange";
      return "green";
      // return "var(--circle-color)";
    },

    getStatusColor(status) {
      switch (status) {
        case "process":
          return "orange";
        case "success":
          return "success";
        case "failed":
          return "error";
        default:
          return "info";
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case "process":
          return "mdi-progress-clock";
        case "success":
          return "mdi-check-circle";
        case "failed":
          return "mdi-close-circle";
        default:
          return "mdi-help-circle";
      }
    },
    getStatusText(status) {
      switch (status) {
        case "process":
          return "در حال ساخت";
        case "success":
          return "ساخت شده";
        case "failed":
          return "ساخت ناموفق";
        default:
          return "تکمیل کردن ساخت";
      }
    },
    async fetchDigikalaData(product) {
      try {
        if (!product) {
          //   "محصولی در دیجی‌کالا یافت نشد. از داده‌های موجود استفاده می‌شود."
          this.cardTexts.singleProduct.name =
            this.cardTexts.singleProduct.name || this.ProductName;
          this.cardTexts.singleProduct.description =
            this.cardTexts.singleProduct.description || "";
          return;
        } else {
          const productInfo = product.data;
          const existingFeatures = this.cardTexts.Features.details[0].value;
          // Map product information to cardTexts.Features
          const newFeatures = [];
          product.details.forEach((item) => {
            if (item.label && item.value) {
              newFeatures.push({
                label: item.label,
                value: item.value,
                showIt: true,
              });
            }
          });
          // if (product.model) {
          //   newFeatures.push({
          //     label: "سریال",
          //     value: productInfo.series,
          //     showIt: !!productInfo.series,
          //   });
          // }
          if (product.model) {
            newFeatures.push({
              label: "برند",
              value: productInfo.brand,
              showIt: !!productInfo.brand,
            });
          }
          if (product.model) {
            newFeatures.push({
              label: "مدل",
              value: product.model,
              showIt: !!productInfo.model,
            });
          }
          if (product.model) {
            newFeatures.push({
              label: "کشور",
              value: product.country,
              showIt: !!productInfo.country,
            });
          }

          this.cardTexts.Features.details[0].value = [
            ...existingFeatures,
            ...newFeatures,
          ];
          // this.cardTexts.Features.details[0].value.push(newFeatures);
          // Map product information to cardTexts.singleProduct
          this.cardTexts.singleProduct.name =
            this.ProductName || productInfo.title;
          this.cardTexts.singleProduct.description =
            productInfo.description ||
            this.cardTexts.singleProduct.description ||
            "";
          this.cardTexts.singleProduct.price = productInfo.price || 0;
          this.cardTexts.singleProduct.discount = productInfo.discount || 0;

          // Extract specifications
          const specs = productInfo.extra || [];

          // Map specifications to new extraFields
          const newSpecItems = specs.map((attr) => {
            if (!!attr.label && !!attr.value)
              return {
                name: attr.label,
                value: attr.value,
              };
          });

          // Assign icons for new specifications
          const icons = await this.assignIcons(
            newSpecItems.map((item) => item.name),
          );

          // Merge new specifications with existing extraFields (avoid duplicates)
          const existingExtraFields = this.cardTexts.extraFields || [];
          const existingNames = new Set(
            existingExtraFields.map((item) => item.name),
          );

          const mergedExtraFields = [
            ...existingExtraFields,
            ...newSpecItems
              .filter((item) => !existingNames.has(item.name)) // Avoid duplicates
              .map((item, index) => ({
                icon: icons[index] || "mdi-information",
                name: item.name,
                value: item.value,
              })),
          ];

          // Update cardTexts.extraFields
          this.cardTexts.extraFields = mergedExtraFields;

          // Map images from Digikala
          const listImages =
            productInfo.gallery[0]?.images?.map((item, index) => ({
              url: item.url,
              title: "",
              name: "",
              id: index + 1,
              description: "",
              link: "",
            })) || [];

          // Merge Digikala images with existing gallery images
          this.cardTexts.photo.gallery = this.cardTexts.photo.gallery
            ? this.cardTexts.photo.gallery
            : [{ name: "گالری تصاویر", images: [] }];
          this.cardTexts.photo.gallery[0].images = [
            ...this.cardTexts.photo.gallery[0].images,
            ...listImages,
          ];
          // this.cardTexts.photo.gallery[0].images.push(
          //   listImages
          // )
          // Set aboutUs
          this.cardTexts.aboutUs =
            !!this.aboutUs && this.aboutUs !== "" ? this.aboutUs : "";

          // Set organizationName
          this.handleToast({
            type: "success",
            message: "اطلاعات محصول با موفقیت دریافت شد",
          });
          // this.$toast.success("اطلاعات محصول با موفقیت دریافت شد");
        }
      } catch (error) {
        console.error("Error fetching Digikala data:", error);
        //this.$toast.error("خطا در دریافت اطلاعات محصول");
      }
    },
    generateUID() {
      return "xxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    },
    filterEnglishOnly(text) {
      return text.replace(/[^a-zA-Z0-9]/g, "");
    },
    async qrUpdate(final = false) {
      this.loading = true;
      try {
        this.cardTexts.aboutUs =
          this.aboutUs !== null ? this.aboutUs : this.cardTexts.aboutUs;
        const data = new FormData();
        data.append(
          "type",
          this.type === "visitcard"
            ? "VisitCard"
            : this.type === "product"
            ? "singleProduct"
            : "labelProduct",
        );
        // data.append("image", this.info.image);
        data.append("qrType", "dynamic");
        data.append("domain", this.createdDomain);
        data.append("identifier", this.info.identifier);
        data.append("_method", "PUT");
        data.append("qr", JSON.stringify(this.info.qr));
        data.append("qr_id", this.info.qrId);
        if (this.type === "visitcard") {
          data.append(
            "data",
            JSON.stringify({
              theme: this.info.data.theme,
              data: this.cardTexts,
              orders: this.info.data.orders,
            }),
          );
        } else if (this.type === "product") {
          data.append(
            "data",
            JSON.stringify({
              theme: this.info.data.theme,
              data: this.cardTexts,
            }),
          );
        } else if (this.type === "label") {
          data.append(
            "data",
            JSON.stringify({
              theme: this.info.data.theme,
              data: this.cardTexts,
            }),
          );
        }
        //fetch data from https://crawler.terminalads.com/api/enamad/manual?url=webzi.ir/&qr_id=4&response=1
        const { data: response } = await this.$api.post(
          "/v1/testQr/update",
          data,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        this.handleToast({
          type: "success",
          message: "کیوآر کارت ویزیت شما باموفقیت ویرایش شد",
        });
        // this.$toast.success("کیوآر کارت ویزیت شما باموفقیت ویرایش شد.");

        if (final) {
          this.$forceUpdate(); //remove this for not go to qr/list
          //  this.goto("qr/list");
          // this.goto("qr/list", { refresh: Date.now() });
        }
      } catch (error) {
        // if (response?.data?.errors) {
        //   if (
        //     response.data.errors.identifier[0] ===
        //       "آدرس کیوآر قبلا انتخاب شده است." &&
        //     response?.status === 422
        //   ) {
        //     const parts = this.filterEnglishOnly(this.dtUrl).split("-");
        //     const lastPart = parts[parts.length - 1];
        //     if (!isNaN(lastPart)) {
        //       parts[parts.length - 1] = Number(lastPart) + 1;
        //       this.dtUrl = parts.join("-");
        //     } else {
        //       this.dtUrl = parts.join("-");
        //       this.dtUrl = `${this.filterEnglishOnly(this.dtUrl)}-0`;
        //       this.markQr();
        //     }
        //   } else {
        //     Object.keys(response.data.errors).forEach((item) => {
        //       this.$toast.error(response.data.errors[item][0], {
        //         timeout: 3000,
        //       });
        //     });
        //   }
        // } else {
        //   // this.$toast.error("ساخت کیوآر با خطا روبرو شد!");
        // }
        // // this.$toast.error("خطا در به‌روزرسانی کیوآر!");
      } finally {
        this.loading = false;
      }
    },
    header() {
      return {
        Authorization: JwtService.getToken()
          ? `Bearer ${JwtService.getToken()}`
          : null,
        Accept: "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Access-Control-Allow-Headers": "*",
        "Content-Type": "application/json",
      };
    },
    async tryprocess(id) {
      if (this.loading) return;
      this.loading = true;
      try {
        await axios.get(`${this.$aiApi}/process/tryAgain/${id}`, {
          headers: this.header(),
        });
        // this.$toast.success("فرآیند با موفقیت تکرار شد.");
        this.handleToast({
          type: "success",
          message: "فرآیند با موفقیت تکرار شد.",
        });
        await this.checkProcess(id); // بررسی وضعیت پس از تلاش مجدد
      } catch (error) {
        console.error("Error retrying process:", error);
        this.handleToast({
          type: "error",
          message: "خطا در تکرار فرآیند.",
        });
        // this.$toast.error("خطا در تکرار فرآیند!");
      } finally {
        this.loading = false;
        this.$nextTick(() => this.$forceUpdate());
      }
    },
    // async checkProcess(id) {
    //   axios
    //     .get(`https://ai-api.terminalads.com/api/process/check/${id}`, {
    //       headers: this.header(),
    //     })
    //     .then(async (data) => {
    //       //response.id
    //       //response.status
    //       //response.error
    //       //response.end_at
    //       //response.payload.payload.data.images
    //       //response.payload.payload.data.title_en
    //       //response.payload.payload.step //for step check
    //       //response.payload.payload.max_step //for check
    //       //response.payload.payload.imagesProcessType
    //       //response.payload.payload.product_info.brand
    //       //response.payload.payload.product_info.title
    //       //response.payload.payload.product_info.details {label value} //for array
    //       //response.payload.payload.product_info.gallery {name images{id,url,title,description,name}} //for array
    //       //response.payload.payload.product_info.options {size //for array, color //for array}
    //       //response.payload.payload.product_info.sub_products {type[] ,count[],weight[]}
    //       //response.payload.payload.product_info.description
    //       //response.payload.payload.data.remove_bg_img //logo
    //       //response.payload.payload.imagesProcessType
    //       const response = data.data;
    //       const newType =
    //         response.payload.payload.imagesProcessType || "visitcard";
    //       if (this.type !== newType) {
    //         this.type = newType;
    //         this.updateCardTextsByType();
    //       }
    //       this.status = response.status;
    //       this.cardTexts.auto_build = response.status;
    //       const englishBusiness = `${response.payload.payload.data.title_en} ${response.payload.payload.data.product_name_en}`;
    //       this.business = `${response.payload.payload.title} ${response.payload.payload.product_name}`;
    //       let val_process =
    //         response.payload.payload.step > response.payload.payload.max_step
    //           ? 100
    //           : (response.payload.payload.step /
    //               response.payload.payload.max_step) *
    //             100;
    //       //set progress bar
    //       this.progressValue = val_process;
    //       this.$forceUpdate();

    //       if (response.status === "success") {
    //         //process //failed
    //         //updateQr
    //         this.Ai_Text = response.payload.payload.data.gen_desc_fa;
    //         const Ai_Text_en = response.payload.payload.data.gen_desc_en;
    //         this.aboutUS = response.payload.payload?.about_us || null;
    //         this.images = response?.payload?.payload?.data?.images || null;
    //         await this.processAiText(
    //           this.type,
    //           this.Ai_Text,
    //           englishBusiness,
    //           Ai_Text_en
    //         );
    //         await this.genenterimageAi(englishBusiness, response);
    //         await this.qrUpdate(true);
    //         this.status = "success";
    //         this.status = "success";
    //         this.cardTexts.auto_build = "success";
    //         // this.$emit("refreshBuildingList");
    //         this.$forceUpdate();
    //         clearInterval(this.progressInterval);
    //       } else if (response.status === "failed") {
    //         this.status = "failed";
    //         this.cardTexts.auto_build = "failed";

    //         //call
    //         this.tryprocess(this.info.data.data.auto_id);
    //         this.$forceUpdate();
    //       } else if (response.status === "process") {
    //         let val_process =
    //           response.payload.payload.step > response.payload.payload.max_step
    //             ? 100
    //             : (response.payload.payload.step /
    //                 response.payload.payload.max_step) *
    //               100;
    //         //set progress bar
    //         this.progressValue = val_process;
    //         this.$forceUpdate();
    //       }
    //     })
    //     .catch((error) => {
    //       console.error("Error retrying process:", error);
    //       this.$toast.error("خطا در ادامه ساخت فرآیند!");
    //       this.cardTexts.auto_build = "failed";
    //       this.status = "failed";
    //       this.$forceUpdate();
    //     });
    // },
    async checkProcess(id) {
      try {
        const { data: response } = await axios.get(
          `${this.$aiApi}/process/check/${id}`,
          { headers: this.header() },
        );

        const newType =
          response.payload.payload.imagesProcessType || "visitcard";
        if (this.type !== newType) {
          this.type = newType;
          this.updateCardTextsByType();
        }

        this.status = response.status;
        this.cardTexts.auto_build = response.status;
        const val_process =
          response.payload.payload.step > response.payload.payload.max_step
            ? 100
            : (response.payload.payload.step /
                response.payload.payload.max_step) *
              100;
        this.progressValue = val_process;
        if (response.status === "success") {
          this.Ai_Text = response.payload.payload.data.gen_desc_fa;
          const Ai_Text_en = response.payload.payload.data.gen_desc_en;
          this.aboutUs = response.payload.payload?.about_us || null;
          this.images = response?.payload?.payload?.data?.images || null;
          await this.processAiText(
            this.type,
            this.Ai_Text,
            `${response.payload.payload.data.title_en} ${response.payload.payload.data.product_name_en}`,
            Ai_Text_en,
          );
          await this.genenterimageAi(
            `${response.payload.payload.data.title_en} ${response.payload.payload.data.product_name_en}`,
            response,
          );
          //upadte gallery
          if (this.type !== "visitcard") {
            const listImages =
              this.images?.map((item, index) => ({
                url: item,
                title: "",
                name: "",
                id: index + 1,
                description: "",
                link: item,
              })) || [];
            this.cardTexts.photo.gallery[0].images = [
              ...this.cardTexts.photo.gallery[0].images,
              ...listImages,
            ];
          }
          // Set aboutUs
          this.cardTexts.aboutUs =
            !!this.aboutUs && this.aboutUs !== ""
              ? this.aboutUs
              : this.cardTexts.aboutUs || "";
          await this.qrUpdate(true);

          this.status = "success";
          this.cardTexts.auto_build = "success";
          this.$emit("refreshBuildingList");
          clearInterval(this.progressInterval);
        } else if (response.status === "failed") {
          this.status = "failed";
          this.cardTexts.auto_build = "failed";
          this.$forceUpdate();
        } else if (response.status === "process") {
          this.status = "process";
          this.cardTexts.auto_build = "process";
        }

        this.$nextTick(() => this.$forceUpdate());
      } catch (error) {
        console.error("Error checking process:", error);
        this.status = "failed";
        this.cardTexts.auto_build = "failed";
        // this.$toast.error("خطا در ادامه ساخت فرآیند!");
        this.handleToast({
          type: "error",
          message: "خطا در ادامه ساخت فرآیند.",
        });
        this.$nextTick(() => this.$forceUpdate());
      }
    },
    cleanHtmlText(htmlText, preserveNewlines = false) {
      if (typeof htmlText !== "string") {
        return "";
      }
      if (!/<[^>]+>/.test(htmlText)) {
        const text = htmlText.trim();
        return preserveNewlines ? text : text.replace(/\s+/g, " ");
      }
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          htmlText || "<div></div>",
          "text/html",
        );
        const text = doc.body.textContent || "";
        return preserveNewlines
          ? text.trim()
          : text.trim().replace(/\s+/g, " ");
      } catch (error) {
        console.error("Error parsing HTML text:", error.message);
        return "";
      }
    },
    async processAiText(type, aiHtml, englishBusiness, englishHtml = null) {
      // Parse HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(aiHtml || "<div></div>", "text/html");
      // Extract title (first heading or fallback)
      const titleElement = doc.querySelector("h1, h2, h3") || {
        textContent: englishBusiness,
      };
      const englishDoc = englishHtml
        ? parser.parseFromString(englishHtml, "text/html")
        : null;
      const persianTitle =
        this.cleanHtmlText(titleElement.textContent) ||
        englishBusiness ||
        type === "visitcard"
          ? "کارت ویزیت"
          : type === "product"
          ? "معرفی محصول"
          : "لیبل محصول";
      // Extract description (first paragraph, div, or fallback)

      const descriptionElement = doc.querySelector("p, div") || {
        textContent: "",
      };

      const persianDescription =
        this.cleanHtmlText(descriptionElement.textContent) ||
        type === "visitcard"
          ? "توضیحات ارائه خدمات حرفه‌ای برای کسب‌وکار شما."
          : type === "product"
          ? "توضیحات ارائه خدمات حرفه‌ای برای معرفی محصول شما."
          : "توضیحات ارائه خدمات حرفه‌ای برای لیبل محصول شما.";
      // Extract features (list items or fallback)
      const listItems = Array.from(doc.querySelectorAll("li, p , div"));
      const items = listItems
        // .slice(0, 5)
        .map((li, index) => {
          // Limit to 5 features
          const strong = li.querySelector("strong, b");
          const englishLi = englishDoc?.querySelectorAll("li, p, div")[index];
          const englishStrong = englishLi?.querySelector("strong, b");
          return {
            title:
              strong?.textContent.trim() ||
              li.textContent.trim().split(":")[0]?.replace(":", "") ||
              "",
            description: strong
              ? this.cleanHtmlText(
                  li.textContent.replace(strong.textContent, ""),
                )
              : this.cleanHtmlText(
                  li.textContent
                    .split(":")
                    .slice(1)
                    .join(":")
                    ?.replace(":", ""),
                ) || li.textContent.trim(),
            original:
              englishStrong?.textContent.trim() ||
              englishLi?.textContent.trim().split(":")[0]?.replace(":", "") ||
              li.textContent.trim(),
          };
        })
        .filter((item) => item.title || item.description); // Remove empty items

      // Translate items to Persian
      const persianItems = await Promise.all(
        items.map(async (item) => ({
          title: item?.title || "ویژگی",
          description: item.description || "توضیحات",
          original: item.title || item.description,
        })),
      );

      if (persianItems.length === 0) {
        try {
          const fallbackTitle = "ویژگی";
          const fallbackDescription = "توضیحات";
          persianItems.push({
            title: fallbackTitle,
            description: fallbackDescription,
            original: "Feature",
          });
        } catch (error) {
          persianItems.push({
            title: "ویژگی",
            description: "توضیحات",
            original: "Feature",
          });
        }
      }
      // Assign icons based on English text
      const icons = await this.assignIcons(
        items.map((item) => item.original || item.title || item.description),
      );

      // Update cardTexts
      this.cardTexts.visitName =
        this.cardTexts.visitName &&
        this.cardTexts.visitName !== "" &&
        this.cardTexts.visitName !== null
          ? this.cardTexts.visitName
          : persianTitle;

      this.cardTexts.aboutUs =
        !!this.aboutUs && this.aboutUs !== ""
          ? this.aboutUs
          : this.cleanHtmlText(persianDescription)
              .replace("html", "")
              .replace("` `", "")
              .replace("HTML", "")
              .replace("`", "");

      this.cardTexts.extraFields = persianItems.map((item, index) => ({
        icon: icons[index] || "mdi-information",
        name: item.title,
        value: item.description,
      }));
      if (type !== "visitcard") {
        this.cardTexts.Features.details[0].name =
          "ویژگی " + this.cardTexts.visitName;

        this.cardTexts.Features.details[0].value = persianItems.map(
          (item, index) => ({
            icon: icons[index] || "mdi-information",
            name: item.title,
            value: item.description,
          }),
        );
      }
      this.cardTexts.auto_build = null;
      this.cardTexts.auto_id = null;
      // Set dtUrl for QR code
      this.dtUrl = englishBusiness.toLowerCase().replace(/\s+/g, "-");
      // set data for information product  for product and label product
      if (type === "product" || type === "labelProduct") {
        this.cardTexts.singleProduct.name =
          this.cardTexts.singleProduct.name || "نام محصول";
        this.cardTexts.singleProduct.description =
          persianDescription || this.cardTexts.singleProduct.description || "";
        this.cardTexts.singleProduct.image =
          this.images?.[1] || this.cardTexts.singleProduct.image;
        this.cardTexts.singleProduct.extra = persianItems.map(
          (item, index) => ({
            label: item.title,
            icon: icons[index] || "mdi-information",
            name: item.title,
            value: item.description,
            link: "",
          }),
        );
      }
    },
    async assignIcons(texts) {
      const iconMap = {
        "scan|scanning|barcode|qr|read|reader|code|qrcode": "mdi-qrcode-scan",
        "precision|accuracy|target|exact|correct|pinpoint|sharp": "mdi-target",
        "durable|durability|resilient|strong|robust|tough|sturdy|hardy":
          "mdi-shield-check",
        "response|speed|fast|quick|rapid|swift|efficient|prompt":
          "mdi-clock-fast",
        "user-friendly|ease|simple|usable|intuitive|easy|accessible|straightforward":
          "mdi-account-check",
        "security|secure|safe|safety|protected|trustworthy|reliable|encrypted":
          "mdi-lock",
        "quality|high-quality|premium|superior|excellent|top-notch|exceptional":
          "mdi-star",
        "innovation|innovative|modern|advanced|cutting-edge|new|forward-thinking":
          "mdi-lightbulb-on",
        "connectivity|connection|network|wireless|bluetooth|internet|linked":
          "mdi-wifi",
        "compatibility|compatible|versatile|flexible|adaptable|universal":
          "mdi-cellphone-link",
        "portable|portability|mobile|lightweight|compact|travel-friendly":
          "mdi-briefcase",
        "battery|power|energy|long-lasting|charge|rechargeable|endurance":
          "mdi-battery",
        "design|style|stylish|elegant|aesthetic|beautiful|sleek|modern":
          "mdi-palette",
        "dimensions|size|compact|large|small|measurement|proportion":
          "mdi-ruler",
        "weight|light|heavy|lightweight|featherweight": "mdi-weight",
        "color|colour|vivid|bright|shade|hue|vibrant": "mdi-format-color-fill",
        "eco-friendly|environment|green|sustainable|recyclable|biodegradable":
          "mdi-leaf",
        "support|customer-service|assistance|help|care|aftercare|service":
          "mdi-headset",
        "versatile|multifunctional|all-in-one|multi-purpose|multi-use":
          "mdi-all-inclusive",
        "technology|tech|digital|smart|intelligent|automated": "mdi-chip",
        "global|worldwide|international|universal|cross-border": "mdi-earth",
        "affordable|cost-effective|budget-friendly|cheap|value|economical":
          "mdi-currency-usd",
        "performance|performant|powerful|strong|effective|high-performance":
          "mdi-gauge",
        "privacy|private|confidential|discreet|secure-data": "mdi-eye-off",
        "installation|setup|easy-to-install|assembly|deploy": "mdi-tools",
        "warranty|guarantee|assurance|certified|backed": "mdi-certificate",
        "accessibility|available|reach|convenient|handy": "mdi-map-marker",
        "stability|stable|dependable|consistent|steady": "mdi-anchor",
        "collaboration|teamwork|partnership|cooperation|synergy":
          "mdi-account-group",
        "information|informative|details|data|insights|knowledge":
          "mdi-information",
        "customization|customizable|personalized|tailored|bespoke": "mdi-cog",
        "scalability|scalable|expandable|grow|growth": "mdi-arrow-expand",
        "reliability|reliable|trusty|dependable|consistent": "mdi-check-circle",
        "automation|automated|self-operating|smart-operation": "mdi-robot",
        "comfort|comfortable|ergonomic|cozy|user-comfort": "mdi-sofa",
        "efficiency|efficient|optimized|streamlined|productive": "mdi-rocket",
        "weather-resistant|waterproof|dustproof|rugged":
          "mdi-weather-partly-cloudy",
        "real-time|live|instant|immediate": "mdi-update",
        "integration|integrated|seamless|connect|unified": "mdi-puzzle",
        "health|wellness|medical|care|hygiene": "mdi-hospital",
        "food|cuisine|menu|dining|culinary": "mdi-food",
        "education|learning|training|knowledge|study": "mdi-school",
        "entertainment|fun|media|leisure|enjoyment": "mdi-television",
      };
      return texts.map((text) => {
        const lowerText = text.toLowerCase();
        for (const [pattern, icon] of Object.entries(iconMap)) {
          const regex = new RegExp(`\\b(${pattern})\\b`, "i");
          if (regex.test(lowerText)) {
            return icon;
          }
        }
        return "mdi-information";
      });
    },
    async genenterimageAi(englishBusiness, response) {
      try {
        if (this.type === "product" || this.type === "label") {
          // Set profile image (logo)
          this.cardTexts.photo.profile =
            this.cardTexts.photo.profile !==
            this.$cdnUrl + "/upload/64b2abe4bab25.webp"
              ? this.cardTexts.photo.profile
              : !!response.payload.payload.data.remove_bg_img
              ? response.payload.payload.data.remove_bg_img
              : this.$cdnUrl + "/upload/64b2abe4bab25.webp";

          // Set head background (use first gallery image if available)
          this.cardTexts.photo.headBackground =
            this.cardTexts.photo.headBackground !==
            this.$cdnUrl + "/upload/64b2abe4bab25.webp"
              ? this.cardTexts.photo.headBackground
              : response.payload.payload.data.images?.[0] ||
                response.payload.payload.product_info.gallery?.[0]?.images?.[0]
                  ?.url ||
                this.cardTexts.photo.headBackground;
          //
          this.cardTexts.photo.background =
            response.payload.payload.data.images?.[0] ||
            response.payload.payload.product_info.gallery?.[0]?.images?.[0]
              ?.url ||
            this.cardTexts.photo.background;
          // Initialize gallery if not already set
          this.cardTexts.photo.gallery = this.cardTexts.photo.gallery || [
            { name: "گالری تصاویر", images: [] },
          ];

          // Map gallery images from product_info
          // const listImages =
          //   response.payload.payload.product_info !== null &&
          //   !!response.payload.payload.product_info?.gallery
          //     ? response.payload.payload.product_info.gallery.flatMap(
          //         (galleryItem, galleryIndex) =>
          //           galleryItem?.images.map((image, index) => ({
          //             url: image?.url || "",
          //             title: image?.title || "",
          //             name: `banner${
          //               galleryIndex * 100 + index + 1
          //             }_${englishBusiness}`,
          //             id: 10001 + galleryIndex * 100 + index,
          //             description: image?.description || "",
          //             link: image?.link || image?.url || "",
          //           }))
          //       )
          //     : [];
          const listImages2 =
            !!response.payload.payload.data !== null &&
            !!response.payload.payload.data?.images
              ? response.payload.payload.data.images.map((image, index) => ({
                  url: image || "",
                  title: index.toString() || "",
                  name: `banner${index + 1}_${englishBusiness}`,
                  id: 10001 + index,
                  description: "",
                  link: image || "",
                }))
              : [];

          // Merge new images with existing gallery, avoiding duplicates
          const existingImageUrls = new Set(
            this.cardTexts.photo.gallery[0].images.map((img) => img.url),
          );
          this.cardTexts.photo.gallery[0].images = [
            ...listImages2,
            ...this.cardTexts.photo.gallery[0].images,
          ];
          // this.cardTexts.description =
          //   response.payload.payload.product_info !== null &&
          //   response.payload.payload.product_info.content !== null
          //     ? response.payload.payload.product_info?.content
          //     : response.payload.payload.product_info !== null &&
          //       response.payload.payload.product_info?.description !== null
          //     ? response.payload.payload.product_info?.description
          //     : this.cardTexts.description;
        } else if (this.type === "visitcard") {
          // Existing visitcard logic remains unchanged
          this.cardTexts.photo.profile =
            this.cardTexts.photo.profile !== "/media/defaults/qrLogo.webp"
              ? this.cardTexts.photo.profile
              : !!this.images["logo_img"]
              ? this.images["logo_img"]
              : "/media/defaults/qrLogo.webp";

          this.cardTexts.photo.headBackground =
            this.cardTexts.photo.headBackground !==
            "/media/defaults/visitFirstDefault.webp"
              ? this.cardTexts.photo.headBackground
              : this.images["main_img"] || this.cardTexts.photo.headBackground;

          this.cardTexts.photo.gallery = this.cardTexts.photo.gallery || [
            { name: "گالری تصاویر", images: [] },
          ];
          this.cardTexts.photo.gallery[0].images = [
            ...(this.cardTexts.photo.gallery[0].images || []),
            {
              url: this.images["banner_img_1"],
              title: "",
              name: `banner1_${englishBusiness}`,
              id: 10001,
              description: "",
              link: "",
            },
            {
              url: this.images["banner_img_2"],
              title: "",
              name: `banner2_${englishBusiness}`,
              id: 10002,
              description: "",
              link: "",
            },
            {
              url: this.images["banner_img_3"],
              title: "",
              name: `banner3_${englishBusiness}`,
              id: 10003,
              description: "",
              link: "",
            },
            {
              url: this.images["banner_img_4"],
              title: "",
              name: `banner4_${englishBusiness}`,
              id: 10004,
              description: "",
              link: "",
            },
          ];
        }
        this.$root.$emit("rerenderSwiper");
      } catch (error) {
        console.error("Error in genenterimageAi:", error);
      }
    },

    // Notification methods
    initializeNotifications() {
      // Load viewed notifications from localStorage
      const viewed = localStorage.getItem(
        `viewedNotifications_${this.info.identifier}`,
      );
      if (viewed) {
        this.viewedNotifications = JSON.parse(viewed);
      } else {
        this.viewedNotifications = [];
      }

      // Load existing notifications from localStorage
      const saved = localStorage.getItem(
        `notifications_${this.info.identifier}`,
      );
      if (saved) {
        this.notifications = JSON.parse(saved);
      } else {
        this.notifications = [];
      }
      // Override toast methods for this specific QR
      // this.overrideToastMethods();
    },

    overrideToastMethods() {
      // Store original toast methods
      const originalToast = {
        success: this.$toast.success,
        error: this.$toast.error,
        info: this.$toast.info,
      };

      // Override success method
      this.$toast.success = (message, options) => {
        if (
          message === "کیوآر کارت ویزیت شما باموفقیت ویرایش شد." ||
          message === "اطلاعات محصول با موفقیت دریافت شد" ||
          message === "فرآیند با موفقیت تکرار شد." ||
          message === "معرفی محصول شما با موفقیت تکمیل شد." ||
          message === "لیست ساخت‌ها با موفقیت به‌روزرسانی شد." ||
          message === "تصویر با موفقیت آپلود شد" ||
          message === "داده‌های QR با موفقیت بارگذاری شد." ||
          message === "کارت ویزیت شما با موفقیت ساخته شد ." ||
          message === "عملیات با موفقیت تکمیل شد." ||
          message === "کد رنگ کپی شد"
        ) {
          this.handleToast({ type: "success", message });
        } else {
          originalToast.success.call(this.$toast, message, options);
        }
      };

      // Override error method
      this.$toast.error = (message, options) => {
        if (
          message === "خطا در دریافت اطلاعات محصول" ||
          message === "خطا در ادامه ساخت فرآیند!" ||
          message === "خطا در تکرار فرآیند!" ||
          message === "خطا در به‌روزرسانی کیوآر!" ||
          message === "خطا در به‌روزرسانی لیست ساخت‌ها:" ||
          message === "خطا در دریافت تصویر!" ||
          message === "خطا در دریافت اطلاعات!" ||
          message === "خطا در پردازش لوگو!" ||
          message === "خطا در ذخیره تصاویر!" ||
          message === "خطا در دریافت اطلاعات کاربر" ||
          message === "خطا در کپی کردن" ||
          message === "ساخت کیوآر با خطا روبرو شد!" ||
          message === "لطفاً فقط فایل‌های JPG یا PNG انتخاب کنید." ||
          message === "اطلاعات را درست وارد کنید." ||
          message === "لطفاً یک تصویر انتخاب کنید."
        ) {
          this.handleToast({ type: "error", message });
        } else {
          originalToast.error.call(this.$toast, message, options);
        }
      };

      // Override info method
      this.$toast.info = (message, options) => {
        if (message === "تلاش مجدد برای ساخت کیوآر کد...") {
          this.handleToast({ type: "info", message });
        } else {
          originalToast.info.call(this.$toast, message, options);
        }
      };
    },

    handleToast({ type, message }) {
      const notification = {
        id: this.generateUID(),
        message,
        type,
        timestamp: new Date().toISOString(),
      };
      this.notifications.unshift(notification); // Add to the top

      // Clear old notifications
      this.clearOldNotifications();

      // Force reactivity update
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    toggleNotificationModal() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications) {
        // Mark all notifications as viewed
        this.notifications.forEach((notification) => {
          if (!this.viewedNotifications.includes(notification.id)) {
            this.viewedNotifications.push(notification.id);
          }
        });
        this.saveViewedNotifications();

        // Force reactivity update
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },

    generateUID() {
      return "xxxx-xxxx-xxxx-xxxx".replace(/[x]/g, () => {
        const r = (Math.random() * 16) | 0;
        return r.toString(16);
      });
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return new Intl.DateTimeFormat("fa-IR", {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: false,
      }).format(date);
    },

    saveNotifications() {
      localStorage.setItem(
        `notifications_${this.info.identifier}`,
        JSON.stringify(this.notifications),
      );
    },
    getNotificationIcon(type) {
      switch (type) {
        case "error":
          return "mdi-alert-circle";
        case "warning":
          return "mdi-alert";
        case "success":
          return "mdi-check-circle";
        case "info":
        default:
          return "mdi-information";
      }
    },
    getNotificationColor(type) {
      switch (type) {
        case "error":
          return "red";
        case "warning":
          return "yellow";
        case "success":
          return "green";
        case "info":
        default:
          return "blue";
      }
    },
    getNotificationClass(type) {
      switch (type) {
        case "error":
          return "notification-error";
        case "warning":
          return "notification-warning";
        case "success":
          return "notification-success";
        case "info":
        default:
          return "notification-info";
      }
    },
    saveViewedNotifications() {
      localStorage.setItem(
        `viewedNotifications_${this.info.identifier}`,
        JSON.stringify(this.viewedNotifications),
      );
    },
    clearAllNotifications() {
      this.notifications = [];
      this.viewedNotifications = [];
      this.saveNotifications();
      this.saveViewedNotifications();
    },

    isNotificationViewed(notificationId) {
      return this.viewedNotifications.includes(notificationId);
    },

    getEyeIcon(notificationId) {
      const isViewed = this.viewedNotifications.includes(notificationId);
      return isViewed ? "mdi-eye" : "mdi-eye-off";
    },

    // Add this method to clear old notifications
    clearOldNotifications() {
      // Keep only last 10 notifications
      if (this.notifications.length > 10) {
        this.notifications = this.notifications.slice(0, 10);
        this.saveNotifications();
      }
    },
  },
};
</script>

<style scoped>
.v-progress-circular {
  --circle-size: 64px;
  --circle-color: linear-gradient(
    90deg,
    red,
    orange,
    yellow,
    green,
    blue,
    indigo,
    violet
  );
}

.v-progress-circular__overlay {
  background: var(--circle-color) !important;
}

.v-progress-circular__underlay {
  stroke: #e0e0e0 !important;
}
.progress-container {
  margin: 0 auto;
  width: 100% !important;
  max-width: 100% !important;
  padding-right: 0px;
  padding-left: 0px;
}

.fontTitle-large {
  font-size: 1.4rem !important;
  font-weight: 700 !important;
  line-height: 1.2;
}

.status-chip {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  min-width: 80px;
  text-align: center;
}

.status-text {
  font-size: 1.1rem;
  font-weight: bold;
}

.error-text {
  color: rgb(252, 180, 180);
}

.loading-text {
  color: rgb(170, 170, 247);
}

.completed-text {
  color: rgb(158, 247, 158);
}

.show {
  display: flex;
  flex-direction: row;
  gap: 5px;
  justify-content: center;
  justify-items: center;
  align-items: center;
}

.cover {
  display: flex;
  gap: 5px;
}

.refresh-btn {
  transition: all 0.3s ease;
  color: white !important;
}

.refresh-btn:hover {
  transform: rotate(360deg);
}

/* Responsive Design */
@media (max-width: 768px) {
  .progress-container {
    padding: 12px !important;
  }

  .fontTitle-large {
    font-size: 1.2rem !important;
  }

  .status-chip {
    font-size: 0.7rem !important;
    min-width: 70px;
  }

  .v-list-item-content {
    padding-right: 8px;
    padding-left: 8px;
  }

  .v-list-item-action {
    margin-left: 8px !important;
  }

  .v-progress-circular {
    width: 48px !important;
    height: 48px !important;
  }
}

@media (max-width: 480px) {
  .progress-container {
    padding: 8px !important;
  }

  .fontTitle-large {
    font-size: 1.1rem !important;
  }

  .status-chip {
    font-size: 0.65rem !important;
    min-width: 60px;
  }

  .v-list-item-avatar {
    margin-right: 8px !important;
  }

  .v-icon {
    font-size: 20px !important;
  }

  .v-progress-circular {
    width: 40px !important;
    height: 40px !important;
  }
}

/* Fix for mobile table layout */
@media (max-width: 768px) {
  .v-list-item-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .d-flex.align-center.justify-space-between {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .d-flex.align-center.justify-end {
    justify-content: flex-start !important;
    margin-top: 8px;
  }
}

/* Notification styles */
.notification-bell {
  transition: transform 0.3s ease;
  position: relative;
  z-index: 10;
}

.notification-bell.shake {
  animation: shake 0.7s ease-in-out infinite;
}

.notification-bell:hover {
  transform: scale(1.1);
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-1px) rotate(-3deg);
  }
  50% {
    transform: translateX(1px) rotate(3deg);
  }
  75% {
    transform: translateX(-1px) rotate(-3deg);
  }
  100% {
    transform: translateX(0);
  }
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.notification-item {
  border-bottom: 1px solid #eee;
  padding: 12px 16px;
  transition: background-color 0.2s ease;
}

.notification-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

@media (max-width: 600px) {
  .notification-list {
    max-height: 70vh;
  }
}
.notification-container {
  display: inline-flex;
  align-items: center;
  margin-left: 16px;
}

.notification-bell {
  font-size: 24px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.bell-shake {
  animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

.notification-panel {
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
  background: linear-gradient(135deg, #ffffff, #f5f7fa);
}

.notification-list {
  max-height: 300px;
  overflow-y: auto;
}

.notification-item {
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s ease;
  padding: 8px;
}

.notification-item:hover {
  filter: brightness(95%);
}

.notification-error {
  background-color: rgba(255, 0, 0, 0.1);
  border: 2px solid #d32f2f;
  border-radius: 8px;
  margin: 2px;
}

.notification-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border: 2px solid #f57c00;
  border-radius: 8px;
  margin: 2px;
}

.notification-success {
  background-color: rgba(0, 128, 0, 0.1);
  border: 2px solid #388e3c;
  border-radius: 8px;
  margin: 2px;
}

.notification-info {
  background-color: rgba(33, 150, 243, 0.1);
  border: 2px solid #1976d2;
  border-radius: 8px;
  margin: 4px;
}

.time-box {
  display: inline-block;
  background-color: #ffffffce;
  border: 1px solid #5e5e5e7e;
  border-radius: 8px;
  padding: 2px;
  font-size: 0.75rem;
  font-weight: 500;
}

.headline {
  font-size: 1.2rem !important;
  font-weight: 600 !important;
  color: #333;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-2px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(2px);
  }
}

/* استایل برای تم تاریک */
.theme--dark .notification-panel {
  background: linear-gradient(135deg, #2c3e50, #34495e);
  color: #ffffff;
}

.theme--dark .notification-item {
  border-bottom: 1px solid #444;
}

.theme--dark .notification-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .time-box {
  background-color: #424242;
  border: 2px solid #fff;
}

@media (max-width: 768px) {
  .notification-container {
    margin-left: 8px;
  }

  .notification-bell {
    font-size: 20px;
  }

  .notification-panel {
    max-width: 300px;
  }

  .time-box {
    font-size: 0.7rem;
    padding: 2px 6px;
  }
}
</style>
